import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    EfeResponse,
    Field,
    LegalRepresentativeServiceRequest,
    LegalRepresentativesResponse,
    View,
} from 'commons/models';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import {formatDateToISO, nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService} from 'services';
import ShareholderLegalRepresentativeForm from './shareholder-legal-representative-form/ShareholderLegalRepresentativeForm';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {fieldCatalogGetter} from 'hooks/FlowHook';

function ShareholderLegalRepresentative() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, expedientId, kycId, fatherKycId, setKycId} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const {setTempShaholderActive, shareholderPersonType} = useRelatedPeopleContext();
    const [auxLegalRepresentative, setAuxLegalRepresentative] = React.useState<any>(undefined);

    async function onSubmit(values: LegalRepresentativeServiceRequest) {
        const request = {...values};
        request.viewId = flow.data.viewId;
        request.expedientId = expedientId;
        request.personTypeKey = PersonTypes.FISICA;
        request.rol = Constants.LEGAL_REPRESENTATIVE;
        request.birthDate = formatDateToISO(values.birthDate);
        request.viewFatherId = flow.data.fatherId;
        request.fatherKycId = kycId;
        try {
            setIsLoading(true);
            const response: EfeResponse<LegalRepresentativeServiceRequest> =
                await ClientService.saveLegalRepresentative(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                validateShareholder();
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function validateShareholder() {
        setTempShaholderActive(false);
        setKycId(-1);
        const path = nextStep(flow, menu);
        if (Constants.MODULE_STEP !== path) {
            setIsLoading(false);
            history.push(path);
        } else {
            updateModuleStep(true);
            flow.data.expandKey = path;
            setIsLoading(false);
        }
    }

    function onChangeHandler() {
        if (PersonTypes.MORAL === shareholderPersonType) {
            history.push(HistoryRoutes.SHAREHOLDER_REPRESENTATIVE);
        }
    }

    async function overrideFields(personTypeKey: string, view: View | undefined) {
        if (personTypeKey && view && view.fields) {
            await setFields(view.fields);
        } else {
            await setFields(viewFields);
        }
    }

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
    }, []);

    React.useEffect(() => {
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            ViewKeys.SHAREHOLDER_REPRESENTATIVE === flow.data.expandKey
        ) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<LegalRepresentativesResponse> =
                        await ClientService.getLegalRepresentatives(kycId);
                    if (
                        response &&
                        response.data &&
                        response.data.data &&
                        response.data.data.length
                    ) {
                        setAuxLegalRepresentative(response.data.data[0]);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxLegalRepresentative(undefined);
                }
            })();
        }
    }, [kycId]);

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: 'Informaci\u00F3n personal del Representante Legal o Apoderado Legal',
        reactElement: (
            <Box mt={8}>
                {!isLoading && (
                    <ShareholderLegalRepresentativeForm
                        viewId={flow.data.viewId}
                        fieldHandler={overrideFields}
                        fields={[
                            ...fieldCatalogGetter(
                                fields.filter((field) => ATTR_NULL !== field.attrName)
                            ),
                        ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                        submitHandler={onSubmit}
                        auxLegalRepresentative={auxLegalRepresentative}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: ViewKeys.SHAREHOLDER_REPRESENTATIVE === flow.data.expandKey,
    };

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion onChange={onChangeHandler} values={[accordion]} />
        </MainContent>
    );
}

export default ShareholderLegalRepresentative;
